import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

const STORAGE_KEY = 'cart-timer';

/**
 * @typedef {Object} CartTimerRefs
 * @property {HTMLElement} timerText - Timer text element
 * @property {HTMLElement} timer - Timer element
 * @property {HTMLElement} expiredText - Expired text element
 */

/**
 * A custom element that displays a cart timer.
 * Persists `{ value, timestamp }` in sessionStorage like `cart-icon` does for `cart-count`.
 *
 * @extends {Component<CartTimerRefs>}
 */
export class CartTimerComponent extends Component {
  requiredRefs = ['timerText', 'timer', 'expiredText'];

  /** @type {boolean} */
  #suppressDisplayObserver = false;

  /** @type {MutationObserver} */
  #displayObserver = new MutationObserver(() => this.#syncDisplayIfNeeded());

  constructor() {
    super();
    this.#setTime();
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {ReturnType<typeof setInterval> | undefined} */
    this.interval = undefined;
    // this.#resumeIfExpiredButCartJustChanged();
    this.startTimer();

    this.#displayObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Header hydration morphs after connect and resets child markup to server HTML.
    requestIdleCallback(() => this.#syncDisplayIfNeeded());

    document.addEventListener(ThemeEvents.cartUpdate, this.#onDocumentCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#displayObserver.disconnect();
    clearInterval(this.interval);
    this.interval = undefined;
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onDocumentCartUpdate);
  }

  /** @returns {boolean} */
  #isShowingExpired() {
    return Boolean(this.refs.expiredText && !this.refs.expiredText.classList.contains('hidden'));
  }

  /**
   * Re-applies display when external DOM updates (e.g. section morph) disagree with sessionStorage.
   */
  #syncDisplayIfNeeded() {
    if (this.#suppressDisplayObserver) return;

    this.#setTime();
    const expired = this.#parseMinutesToSeconds(this.currentTime) <= 0;
    const showingExpired = this.#isShowingExpired();

    if (expired === showingExpired) return;

    this.startTimer();
  }

  /** @returns {string} */
  #getConfiguredMinutes() {
    return this.dataset?.time ?? this.getAttribute('data-time') ?? '0';
  }

  /**
   * Reads configured minutes from data-time, overridden by sessionStorage when present.
   */
  #setTime() {
    this.currentTime = this.#getConfiguredMinutes();
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw === null || raw === '') return;
      const { value } = JSON.parse(raw);
      this.currentTime = value;
    } catch (_) {}
  }

  /**
   * @param {string} value
   * @param {boolean} [preserveTimestamp] - Keep existing storage timestamp when true
   */
  #persistSession(value, preserveTimestamp = false) {
    try {
      let timestamp = Date.now();
      if (preserveTimestamp) {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
          const { timestamp: ts } = JSON.parse(raw);
          if (typeof ts === 'number') timestamp = ts;
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ value, timestamp }));
    } catch (_) {}
  }

  startTimer() {
    clearInterval(this.interval);
    this.interval = undefined;

    const timeLeft = this.#parseMinutesToSeconds(this.currentTime);

    if (timeLeft <= 0) {
      this.endTimer();
      return;
    }

    const timer = this.refs.timer;

    if (!timer) return;

    this.#suppressDisplayObserver = true;
    this.refs.expiredText?.classList.add('hidden');
    this.refs.timerText?.classList.remove('hidden');
    this.#suppressDisplayObserver = false;

    let secondsRemaining = timeLeft;

    const render = () => {
      const minutes = Math.floor(secondsRemaining / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (secondsRemaining % 60).toString().padStart(2, '0');
      timer.textContent = `${minutes}:${seconds}`;
    };

    render();

    this.interval = setInterval(() => {
      secondsRemaining--;

      render();

      const minutesRemaining = Math.max(0, secondsRemaining) / 60;
      this.currentTime = String(minutesRemaining);

      this.#persistSession(this.currentTime, true);

      if (secondsRemaining <= 0) {
        this.endTimer();
      }
    }, 1000);
  }

  endTimer() {
    clearInterval(this.interval);
    this.interval = undefined;

    this.#suppressDisplayObserver = true;
    this.refs.expiredText?.classList.remove('hidden');
    this.refs.timerText?.classList.add('hidden');
    this.#suppressDisplayObserver = false;

    this.#persistSession('0', true);
  }

  #onDocumentCartUpdate = () => {
    clearInterval(this.interval);
    this.interval = undefined;
    this.currentTime = this.#getConfiguredMinutes();
    this.#persistSession(this.currentTime, false);
    this.startTimer();
  };

  #resumeIfExpiredButCartJustChanged() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw === null || raw === '') return;
      const { value } = JSON.parse(raw);
      if (this.#parseMinutesToSeconds(value) > 0) return;

      this.currentTime = this.#getConfiguredMinutes();
      this.#persistSession(this.currentTime, false);
    } catch (_) {}
  }

  /**
   * @param {string} minutesStr
   * @returns {number} Whole seconds remaining (0 if invalid)
   */
  #parseMinutesToSeconds(minutesStr) {
    const fromData = Number(minutesStr);
    if (!Number.isFinite(fromData) || fromData < 0) {
      return 0;
    }
    return Math.floor(fromData * 60);
  }
}

if (!customElements.get('cart-timer')) {
  customElements.define('cart-timer', CartTimerComponent);
}
